/**
 * N4 · Competência 2 (Álgebra) · task-model H1.10.K01
 *
 *   "Calibrar a balança: deixar um saco sozinho num prato, só pesos no outro, e ela reta."
 *
 * A CONSTRUÇÃO DA ÁLGEBRA. Onde a bissetriz dá um cinto de ferramentas e pede uma
 * figura, aqui o cinto é de operações e o que se pede é uma equação resolvida: tirar um
 * peso de cada prato, tirar um saco de cada prato, repartir os dois pratos em partes
 * iguais. Cada gesto é um passo da resolução, a trajetória fica gravada, e a economia
 * de movimentos vale estrelas — a mesma máquina, o mesmo contrato.
 *
 * A BALANÇA INCLINA, ao contrário da prancha da gangorra. Lá, inclinar entregaria a
 * resposta. Aqui o aluno já sabe que os pratos valem o mesmo — é a hipótese do
 * problema —, e a inclinação é a consequência do que ele acabou de fazer: tirou de um
 * lado só, e vê na hora. É a própria definição de equação, encenada.
 *
 * A RESPOSTA É A BALANÇA CALIBRADA, por qualquer caminho. Até 2026-09-22 a correção
 * exigia que saísse a mesma coisa dos dois pratos, de cada tipo de peça, para que
 * tirar peças até a balança ficar reta não passasse por álgebra. O autor resolveu a
 * questão assim — um recipiente de um lado, os pesos que valem o mesmo do outro — e
 * recebeu "tirou de um prato só" com a balança reta no fim. Decidiu que o item pede a
 * calibragem: a balança torta no fim é o erro; o caminho é do aluno. A trajetória
 * continua gravada, e a economia dela vale estrelas — com a regra escrita no
 * enunciado e a meta no caminho mais curto que existe (`caminhoMaisCurto`).
 *
 * Por que o número de sacos a repartir é sempre 2 ou 3, e nunca 1: repartir é a
 * operação que fecha a conta, e uma variante em que ela não é necessária seria outro
 * item, mais fácil e sem o passo que dá nome ao procedimento.
 */

import { formatBR } from '../../core/num.ts';
import type { Rng } from '../../core/rng.ts';
import { caminhoMaisCurto, ladoQuePende, sacoSozinho } from '../../engine/grade.ts';
import { describeBalanca } from '../../render/balanca.ts';
import { conta, de, lista, nome, osN, umA, verbo, type NomeId } from '../../core/pt.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  BAL_PAROU_ANTES_DE_REPARTIR,
  BAL_SEM_SACO,
  BAL_SOBROU_PESO,
  BAL_UM_LADO_SO,
} from '../../schema/errors.ts';
import type { AcaoBalanca, BalanceTarget, EstadoBalanca, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type Inline } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

interface Contexto {
  readonly texto: string;
  /** O recipiente, como chave do léxico: a concordância sai dele, e não de um acaso. */
  readonly peca: NomeId;
}

const CONTEXTOS: readonly Contexto[] = [
  {
    texto:
      'Numa feira de ciências, uma balança de dois pratos está equilibrada. Em cada prato há saquinhos fechados, todos com a mesma quantidade de bolinhas de gude, e pesos de 1 kg.',
    peca: 'saquinho',
  },
  {
    texto:
      'Numa oficina, uma balança de dois pratos está equilibrada. Em cada prato há potes fechados, todos com a mesma quantidade de parafusos, e pesos de 1 kg.',
    peca: 'pote',
  },
  {
    texto:
      'Numa loja de grãos, uma balança de dois pratos está equilibrada. Em cada prato há sacos fechados, todos com a mesma quantidade de grãos, e pesos de 1 kg.',
    peca: 'saco',
  },
];

export interface Params {
  readonly contexto: number;
  /** Sacos e pesos do prato esquerdo. */
  readonly a: number;
  readonly b: number;
  /** Sacos e pesos do prato direito. */
  readonly c: number;
  readonly d: number;
  /** Quanto vale um saco. */
  readonly x: number;
}

const inicialDe = (p: Params): EstadoBalanca => ({
  esq: { sacos: p.a, pesos: p.b },
  dir: { sacos: p.c, pesos: p.d },
});

const n0 = (v: number): string => formatBR(v, 0);

/**
 * Um caminho em palavras, com as ações repetidas juntas: "tirar 1 saco do prato da
 * direita", "tirar 2 pesos do prato da esquerda", "repartir os dois pratos em 3".
 */
function passosDe(acoes: readonly AcaoBalanca[], peca: NomeId): string[] {
  const out: string[] = [];
  for (let i = 0; i < acoes.length;) {
    const a = acoes[i] as AcaoBalanca;
    if (a.t === 'repartir') {
      out.push(`repartir os dois pratos em ${n0(a.partes)}`);
      i++;
      continue;
    }
    let n = 0;
    while (i < acoes.length) {
      const b = acoes[i] as AcaoBalanca;
      if (b.t !== a.t || b.prato !== a.prato) break;
      n++;
      i++;
    }
    const lado = a.prato === 'esq' ? 'esquerda' : 'direita';
    out.push(`tirar ${conta(n, a.t === 'tirar-saco' ? peca : 'peso')} do prato da ${lado}`);
  }
  return out;
}

function drawParams(rng: Rng): Params {
  for (let tentativa = 0; tentativa < 80; tentativa++) {
    const x = rng.int(2, 5);
    // Sacos a repartir no fim: nunca 1, senão a operação que fecha a conta não aparece.
    const k = rng.pick([2, 3]);
    const c = rng.int(0, 2);
    const a = c + k;
    // Pelo menos um peso do lado dos sacos: é o que obriga a limpar antes de repartir.
    const b = rng.int(1, 3);
    const d = b + k * x;
    if (a > 4 || d > 12) continue;
    return { contexto: rng.int(0, CONTEXTOS.length - 1), a, b, c, d, x };
  }
  return { contexto: 0, a: 3, b: 2, c: 1, d: 10, x: 4 };
}

function alvoDe(peca: NomeId, inicial: EstadoBalanca): BalanceTarget {
  const soPesos = (pr: { sacos: number; pesos: number }): boolean => pr.sacos === 0 && pr.pesos > 0;

  return {
    describe: [
      txt(
        `Tire peças dos pratos até sobrar ${umA(peca).adj('sozinho')} num deles e só pesos no outro, `,
      ),
      txt('com a balança equilibrada no fim.'),
    ],
    diagnostics: [
      {
        // O erro que dá nome ao item, e o único que a balança encena sozinha: parar
        // com ela torta.
        error: BAL_UM_LADO_SO,
        test: (e) => ladoQuePende(inicial, e) !== 'nenhum',
      },
      {
        error: BAL_SEM_SACO,
        test: (e) => e.esq.sacos === 0 && e.dir.sacos === 0,
      },
      {
        // Parou em "2x = 8": faltou repartir.
        error: BAL_PAROU_ANTES_DE_REPARTIR,
        test: (e) =>
          (e.esq.sacos > 1 && e.esq.pesos === 0 && soPesos(e.dir)) ||
          (e.dir.sacos > 1 && e.dir.pesos === 0 && soPesos(e.esq)),
      },
      {
        // Chegou a um saco, mas deixou peso junto dele: "x + 1 = 5" não diz quanto vale x.
        error: BAL_SOBROU_PESO,
        test: (e) =>
          (e.esq.sacos === 1 && e.esq.pesos > 0) || (e.dir.sacos === 1 && e.dir.pesos > 0),
      },
    ],
    partialCredit: [
      {
        id: 'equilibrio',
        label: 'A balança terminou equilibrada',
        weight: 0.4,
        // Saber que o que sai de um prato tem de sair, pesando o mesmo, do outro é
        // metade do procedimento — e é a metade que sobrevive fora desta balança.
        test: (e) => ladoQuePende(inicial, e) === 'nenhum',
      },
      {
        id: 'saco-sozinho',
        label: `Sobrou ${umA(peca).adj('sozinho')} num prato`,
        weight: 0.6,
        test: (e) => sacoSozinho(e) !== null,
      },
    ],
    analyze(e) {
      const out: Inline[] = [];
      const pende = ladoQuePende(inicial, e);
      if (pende !== 'nenhum') {
        const pesado = pende === 'esq' ? 'esquerda' : 'direita';
        out.push(
          txt(
            `A balança terminou pendendo para a ${pesado}: o que saiu do prato da ${pesado} pesava menos que o que saiu do outro. `,
          ),
          txt(
            'Para a balança continuar reta, o que sai de um prato tem de pesar o mesmo que o que sai do outro.',
          ),
        );
        return out;
      }
      if (sacoSozinho(e) === null) {
        out.push(
          txt(
            `Você parou com ${conta(e.esq.sacos, peca)} e ${conta(e.esq.pesos, 'peso')} num prato, ` +
              `e ${conta(e.dir.sacos, peca)} e ${conta(e.dir.pesos, 'peso')} no outro. `,
          ),
          txt(`Falta chegar a ${umA(peca).adj('sozinho')} de um lado.`),
        );
      }
      return out;
    },
  };
}

export const balancaEquacao: ItemTemplate<Params, number> = {
  id: templateId('h1-10-K01-balanca'),
  // v2: a balança pende pelo peso; repartir só com ela reta; o item pede a balança
  // calibrada por qualquer caminho, e não mais a mesma coisa tirada dos dois lados; o
  // comando perde a pergunta do peso; a meta de estrelas é o caminho mais curto, e a
  // regra das estrelas está no enunciado.
  version: 2,
  taskModel: taskModelCode('H1.10.K01'),
  skills: [skillCode('H1.10'), skillCode('H1.6')],
  axis: 'II',
  level: 'N4',
  title: 'A balança de dois pratos',
  responseModel: 'GPCM',
  difficultySources: ['steps.3plus', 'construction.long'],
  params: drawParams,
  solve: (p) => p.x,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    // Sacos dos dois lados: é preciso enxergar que eles também saem aos pares.
    if (p.c > 0) fontes.push('inverse-problem');
    if (p.a - p.c === 3) fontes.push('non-round-numbers');
    return fontes;
  },
  validate(_b, p) {
    if (p.b + (p.a - p.c) * p.x !== p.d) return 'os pratos não começam equilibrados';
    if (p.a - p.c < 2) return 'não sobra o que repartir no fim';
    if (p.x < 2) return 'o recipiente pesa menos de 2';
    return null;
  },
  build(p, x) {
    const ctx = CONTEXTOS[p.contexto] as Contexto;
    const inicial = inicialDe(p);
    const k = p.a - p.c;
    const melhor = caminhoMaisCurto(inicial);
    const meta = melhor.length;
    const desc = describeBalanca(inicial.esq, inicial.dir, ctx.peca, 'x', 'kg', 'nenhum');
    /** "3x", e "x" sozinho quando o coeficiente é 1 — é como se escreve em álgebra. */
    const termo = (k: number): string => `${String(k).replace(/^1$/, '')}x`;

    return {
      stem: [
        txt(ctx.texto),
        br,
        // x É O PESO. O texto dizia "Em cada saco há x grãos" — uma QUANTIDADE — e o
        // comando perguntava quantos QUILOS, com "x = 4 kg" na resolução. Os grãos,
        // parafusos e bolinhas continuam no texto de abertura: são eles que explicam por
        // que todos os recipientes pesam o mesmo.
        txt(`Cada ${nome(ctx.peca)} ${verbo('pesar.pres', 'sg')} `),
        strong('x'),
        // O item não pede o valor de x: pede o gesto que o isola — deixar a balança
        // com um recipiente sozinho de um lado, equilibrada. O número sai disso, na
        // resolução, mas não é a pergunta.
        txt(
          ' quilos. Como a balança está equilibrada, ela só continua equilibrada se o que sair de um prato pesar o mesmo que o que sair do outro.',
        ),
        br,
        // A REGRA DAS ESTRELAS, escrita. Sem ela, o contador de movimentos convidava a
        // uma economia cujas regras o aluno não conhecia — e "repartir conta um só"
        // não é óbvio para quem tiraria as mesmas peças à mão. No papel esta frase sai
        // (ver `TROCAS` em `app/prova.ts`).
        txt(
          'Cada peça tirada conta um movimento, e repartir os dois pratos em partes iguais conta um movimento só. Quanto menos movimentos, mais estrelas.',
        ),
      ],
      comando: [
        txt('Mexa nos pratos até sobrar '),
        strong(String(umA(ctx.peca).adj('sozinho'))),
        txt(' num deles e só pesos no outro.'),
      ],
      stimuli: [],
      interaction: {
        kind: 'balance-ops',
        descricao: desc,
        inicial,
        recipiente: ctx.peca,
        incognita: 'x',
        unidade: 'kg',
        meta,
        valor: x,
        alvo: alvoDe(ctx.peca, inicial),
      },
      dicas: [
        [
          txt('Comece pelos '),
          strong('pesos'),
          txt(
            ` que estão do mesmo lado ${de(osN(ctx.peca))}: tire um de cada prato, de cada vez, e veja a balança voltar ao lugar.`,
          ),
        ],
        [
          txt(`Quando um prato ficar só com ${nome(ctx.peca, 'pl')} e o outro só com pesos, `),
          strong(`reparta os dois em ${n0(k)} partes iguais`),
          txt(' — o botão acende sozinho quando isso é possível.'),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(
          `Em símbolos, a balança diz ${termo(p.a)} ${p.b > 0 ? `+ ${n0(p.b)} ` : ''}= ${p.c > 0 ? `${termo(p.c)} + ` : ''}${n0(p.d)}.`,
        ),
        br,
        txt(
          `Tirando ${conta(p.b, 'peso')} de cada prato, sobram ${termo(p.a)} = ${p.c > 0 ? `${termo(p.c)} + ` : ''}${n0(p.d - p.b)}.` +
            (p.c > 0
              ? ` Tirando ${conta(p.c, ctx.peca)} de cada prato, sobram ${termo(k)} = ${n0(p.d - p.b)}.`
              : ''),
        ),
        br,
        txt(
          `Repartindo os dois pratos em ${n0(k)} partes iguais: x = ${n0(p.d - p.b)} ÷ ${n0(k)} = `,
        ),
        strong(`${n0(x)} kg`),
        txt('.'),
        br,
        txt(
          `O caminho mais curto tem ${conta(meta, 'movimento')}: ${lista(passosDe(melhor, ctx.peca))}.`,
        ),
      ],
      declared: p.c > 0 ? 'dificil' : 'media',
    };
  },
};
